// /home/labdoo/zitech-nexus/frontend/src/pages/admin/TrainingModuleForm.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminTrainingAPI } from '../../services/api';
import './TrainingManagement.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Save: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Cancel: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Spinner: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tm-spinner-small">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
};

const TrainingModuleForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!id);
  const [formData, setFormData] = useState({
    technology_name: '',
    title: '',
    description: '',
    difficulty: 'beginner',
    duration_hours: 1,
    order: 0,
    is_active: true,
  });

  useEffect(() => {
    if (id) {
      fetchModule();
    }
  }, [id]);

  const fetchModule = async () => {
    try {
      const response = await adminTrainingAPI.getModule(parseInt(id!));
      const module = response.data;
      setFormData({
        technology_name: module.technology_name,
        title: module.title,
        description: module.description,
        difficulty: module.difficulty,
        duration_hours: module.duration_hours,
        order: module.order,
        is_active: module.is_active,
      });
    } catch (error) {
      console.error('Error fetching module:', error);
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await adminTrainingAPI.updateModule(parseInt(id!), formData);
      } else {
        await adminTrainingAPI.createModule(formData);
      }
      navigate('/dashboard/admin/training/modules');
    } catch (error) {
      console.error('Error saving module:', error);
      alert('Failed to save module');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="tm-loading-container">
        <div className="tm-spinner">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tm-spinner-svg">
            <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        </div>
        <p className="tm-loading-text">Loading module...</p>
      </div>
    );
  }

  return (
    <div className="tm-form-container">
      {/* Header */}
      <div className="tm-form-header">
        <div className="tm-form-header-left">
          <button className="tm-back-btn" onClick={() => navigate('/dashboard/admin/training/modules')}>
            <Icons.Back />
            Back to Modules
          </button>
          <h1 className="tm-form-title">{id ? 'Edit Module' : 'Create New Module'}</h1>
        </div>
        <button className="tm-btn-secondary" onClick={() => navigate('/dashboard/admin/training/modules')}>
          <Icons.Cancel />
          Cancel
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="tm-form">
        <div className="tm-form-grid">
          {/* Left Column */}
          <div className="tm-form-left">
            <div className="tm-form-card">
              <h3 className="tm-form-card-title">Module Information</h3>
              
              <div className="tm-form-group">
                <label className="tm-form-label">Technology Name *</label>
                <input
                  type="text"
                  className="tm-form-input"
                  required
                  value={formData.technology_name}
                  onChange={(e) => setFormData({ ...formData, technology_name: e.target.value })}
                  placeholder="e.g., React, Python, Django"
                />
              </div>

              <div className="tm-form-group">
                <label className="tm-form-label">Module Title *</label>
                <input
                  type="text"
                  className="tm-form-input"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., React Fundamentals"
                />
              </div>

              <div className="tm-form-group">
                <label className="tm-form-label">Description *</label>
                <textarea
                  className="tm-form-textarea"
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this module covers..."
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="tm-form-right">
            <div className="tm-form-card">
              <h3 className="tm-form-card-title">Settings</h3>
              
              <div className="tm-form-group">
                <label className="tm-form-label">Difficulty</label>
                <select
                  className="tm-form-select"
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div className="tm-form-group">
                <label className="tm-form-label">Duration (hours)</label>
                <input
                  type="number"
                  className="tm-form-input"
                  min="0.5"
                  step="0.5"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData({ ...formData, duration_hours: parseFloat(e.target.value) })}
                />
              </div>

              <div className="tm-form-group">
                <label className="tm-form-label">Order</label>
                <input
                  type="number"
                  className="tm-form-input"
                  min="0"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                />
              </div>

              <div className="tm-form-group tm-form-checkbox">
                <label className="tm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span className="tm-checkbox-text">Active (visible to users)</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="tm-form-actions">
              <button 
                type="submit" 
                className="tm-btn-primary tm-btn-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Icons.Spinner />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icons.Save />
                    {id ? 'Update Module' : 'Create Module'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TrainingModuleForm;